import { useState } from 'react';
import {
  X,
  Upload,
  FileText,
  Image,
  File,
  Shield,
  Loader2,
} from 'lucide-react';

interface FileUploadModalProps {
  onClose: () => void;
  category: string;
  onUpload: (fileData: UploadedFile) => void | Promise<void>;
}

interface UploadedFile {
  file: File;
  category: string;
  issuer: string;
  visibility: string[];
  notes: string;
}

export function FileUploadModal({
  onClose,
  category,
  onUpload,
}: FileUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileCategory, setFileCategory] = useState(category);
  const [issuer, setIssuer] = useState('owner');
  const [visibility, setVisibility] = useState<string[]>(['veterinarians']);
  const [notes, setNotes] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const categories = [
    'Vaccine Certificate',
    'Lab Results',
    'Prescription',
    'Grooming Report',
    'X-Ray / Imaging',
    'Health Certificate',
    'Other',
  ];

  const professionalRoles = [
    'Veterinarians',
    'Groomers',
    'Trainers',
    'Boarding Facilities',
  ];

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploadError(null);
    setUploading(true);
    try {
      await onUpload({
        file: selectedFile,
        category: fileCategory,
        issuer,
        visibility,
        notes,
      });
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = () => {
    if (!selectedFile)
      return <Upload className="w-12 h-12 text-gray-400 dark:text-white/40" />;

    const type = selectedFile.type;
    if (type.startsWith('image/'))
      return <Image className="w-12 h-12 text-blue-600 dark:text-blue-400" />;
    if (type === 'application/pdf')
      return <FileText className="w-12 h-12 text-red-600 dark:text-red-400" />;
    return <File className="w-12 h-12 text-gray-600 dark:text-white/60" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const toggleVisibility = (role: string) => {
    if (visibility.includes(role)) {
      setVisibility(visibility.filter((v) => v !== role));
    } else {
      setVisibility([...visibility, role]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-white/8 flex-shrink-0">
          <h3
            className="text-xl text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            Attach Document
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-white/50 hover:text-gray-600 dark:hover:text-white/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-auto p-6 space-y-6"
        >
          {/* File Upload Area */}
          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Select File
            </label>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : selectedFile
                    ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30'
              }`}
            >
              <div className="flex flex-col items-center">
                {getFileIcon()}
                {selectedFile ? (
                  <div className="mt-4">
                    <div
                      className="mb-1 text-gray-900 dark:text-white"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {selectedFile.name}
                    </div>
                    <div
                      className="text-sm text-gray-500 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {formatFileSize(selectedFile.size)}
                    </div>
                  </div>
                ) : (
                  <>
                    <p
                      className="text-gray-600 dark:text-white/70 mb-2 mt-4"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Click to upload or drag and drop
                    </p>
                    <p
                      className="text-sm text-gray-500 dark:text-white/50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      PDF, JPG, PNG, DOC up to 10MB
                    </p>
                  </>
                )}
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <label
                  htmlFor="file-upload"
                  className="mt-4 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 cursor-pointer"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                >
                  {selectedFile ? 'Change File' : 'Choose File'}
                </label>
              </div>
            </div>
          </div>

          {/* Category */}
          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Document Category
            </label>
            <select
              value={fileCategory}
              onChange={(e) => setFileCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Issuer */}
          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Issued By (Optional)
            </label>
            <select
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <option value="owner">Self (Pet Owner)</option>
              <option value="vet">Veterinarian</option>
              <option value="groomer">Groomer</option>
              <option value="trainer">Trainer</option>
              <option value="other">Other Professional</option>
            </select>
          </div>

          {/* Visibility Permissions */}
          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Visibility Permissions
            </label>
            <p
              className="text-xs text-gray-500 dark:text-white/50 mb-3"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Select who can view this document
            </p>
            <div className="space-y-2">
              {professionalRoles.map((role) => (
                <label
                  key={role}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={visibility.includes(role.toLowerCase())}
                    onChange={() => toggleVisibility(role.toLowerCase())}
                    className="w-4 h-4 text-blue-600 dark:text-blue-400 rounded"
                  />
                  <span
                    className="text-sm text-gray-900 dark:text-white"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {role}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label
              className="block text-sm text-gray-600 dark:text-white/70 mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent resize-none bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40"
              style={{ fontFamily: 'Inter, sans-serif' }}
              rows={3}
              placeholder="Add any additional notes about this document..."
            />
          </div>

          {/* Blockchain Info */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span
                className="text-sm text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                Blockchain Verification
              </span>
            </div>
            <p
              className="text-xs text-gray-700 dark:text-purple-100"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              File will be encrypted and stored off-chain. A cryptographic hash
              will be stored on Solana blockchain for verification. Only users
              with your permission can access the document.
            </p>
          </div>

          {uploadError && (
            <div
              className="rounded-lg p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {uploadError}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white disabled:opacity-50"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedFile || uploading}
              className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Upload & Attach'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
